import React, { useState, useEffect } from 'react';
import { BasicInfo } from '../types';
import {
  StepContainer,
  StepTitle,
  StepDescription,
  FormContainer,
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FormTextarea,
  FormRow,
  FormColumn,
  LocationInput,
  ValidationMessage,
  RequiredIndicator
} from '../../../styles/Profile/ProfileSetup.styles';
import { DateOfBirthSelector, LocationSelector } from '../../shared';

interface BasicInfoStepProps {
  data: Partial<BasicInfo>;
  onChange: (data: BasicInfo, isValid: boolean) => void;
  onNext: () => void;
  onBack: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

export const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
  data,
  onChange,
  onNext,
  onBack,
  isFirstStep,
  isLastStep
}) => {
  const [formData, setFormData] = useState<BasicInfo>({
    firstName: '',
    lastName: '',
    dateOfBirth: new Date(),
    gender: 'other',
    location: {
      city: '',
      state: '',
      country: ''
    },
    occupation: '',
    education: '',
    ...data
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = (field: string, value: any): string => {
    switch (field) {
      case 'firstName':
        if (!value || value.trim().length < 2) {
          return 'First name must be at least 2 characters';
        }
        break;
      case 'lastName':
        if (!value || value.trim().length < 2) {
          return 'Last name must be at least 2 characters';
        }
        break;
      case 'dateOfBirth': {
        if (!value) {
          return 'Date of birth is required';
        }
        const age = new Date().getFullYear() - new Date(value).getFullYear();
        if (age < 18) {
          return 'You must be at least 18 years old';
        }
        if (age > 100) {
          return 'Please enter a valid date of birth';
        }
        break;
      }
      case 'gender':
        if (!value) {
          return 'Gender is required';
        }
        break;
      case 'location.city':
        if (!value || value.trim().length < 2) {
          return 'City is required';
        }
        break;
      case 'location.state':
        if (!value || value.trim().length < 2) {
          return 'State is required';
        }
        break;
      case 'location.country':
        if (!value || value.trim().length < 2) {
          return 'Country is required';
        }
        break;
      case 'occupation':
        if (!value || value.trim().length < 2) {
          return 'Occupation is required';
        }
        break;
      case 'education':
        if (!value || value.trim().length < 2) {
          return 'Education is required';
        }
        break;
      default:
        return '';
    }
    return '';
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => {
      if (field.startsWith('location.')) {
        const locationField = field.split('.')[1];
        return {
          ...prev,
          location: {
            ...prev.location,
            [locationField]: value
          }
        };
      }
      return {
        ...prev,
        [field]: value
      };
    });

    setTouched(prev => ({ ...prev, [field]: true }));
    
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const validateForm = (): boolean => {
    const requiredFields = [
      'firstName', 'lastName', 'dateOfBirth', 'gender',
      'location.city', 'location.state', 'location.country',
      'occupation', 'education'
    ];

    const newErrors: Record<string, string> = {};
    let isValid = true;

    requiredFields.forEach(field => {
      const error = validateField(field, field.startsWith('location.') 
        ? formData.location[field.split('.')[1] as keyof typeof formData.location]
        : formData[field as keyof BasicInfo]
      );
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  useEffect(() => {
    const isValid = validateForm();
    onChange(formData, isValid);
  }, [formData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <StepContainer>
      <StepTitle>Tell us about yourself</StepTitle>
      <StepDescription>
        We need some basic information to create your profile. This information will help others discover you.
      </StepDescription>

      <FormContainer onSubmit={handleSubmit}>
        <FormRow>
          <FormColumn>
            <FormGroup>
              <FormLabel>
                First Name <RequiredIndicator>*</RequiredIndicator>
              </FormLabel>
              <FormInput
                type="text"
                value={formData.firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
                placeholder="Enter your first name"
                $hasError={touched.firstName && !!errors.firstName}
              />
              {touched.firstName && errors.firstName && (
                <ValidationMessage>{errors.firstName}</ValidationMessage>
              )}
            </FormGroup>
          </FormColumn>

          <FormColumn>
            <FormGroup>
              <FormLabel>
                Last Name <RequiredIndicator>*</RequiredIndicator>
              </FormLabel>
              <FormInput
                type="text"
                value={formData.lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
                placeholder="Enter your last name"
                $hasError={touched.lastName && !!errors.lastName}
              />
              {touched.lastName && errors.lastName && (
                <ValidationMessage>{errors.lastName}</ValidationMessage>
              )}
            </FormGroup>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <FormGroup>
              <FormLabel>
                Date of Birth <RequiredIndicator>*</RequiredIndicator>
              </FormLabel>
              <DateOfBirthSelector
                value={formData.dateOfBirth}
                onChange={(date) => handleFieldChange('dateOfBirth', date)}
                hasError={touched.dateOfBirth && !!errors.dateOfBirth}
              />
              {touched.dateOfBirth && errors.dateOfBirth && (
                <ValidationMessage>{errors.dateOfBirth}</ValidationMessage>
              )}
            </FormGroup>
          </FormColumn>

          <FormColumn>
            <FormGroup>
              <FormLabel>
                Gender <RequiredIndicator>*</RequiredIndicator>
              </FormLabel>
              <FormSelect
                value={formData.gender}
                onChange={(e) => handleFieldChange('gender', e.target.value)}
                $hasError={touched.gender && !!errors.gender}
              >
                <option value="">Select your gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="other">Other</option>
              </FormSelect>
              {touched.gender && errors.gender && (
                <ValidationMessage>{errors.gender}</ValidationMessage>
              )}
            </FormGroup>
          </FormColumn>
        </FormRow>

        <FormGroup>
          <FormLabel>
            Location <RequiredIndicator>*</RequiredIndicator>
          </FormLabel>
          <LocationSelector
            value={formData.location}
            onChange={(location) => {
              handleFieldChange('location.city', location.city);
              handleFieldChange('location.state', location.state);
              handleFieldChange('location.country', location.country);
            }}
            hasError={
              (touched['location.city'] && !!errors['location.city']) ||
              (touched['location.state'] && !!errors['location.state']) ||
              (touched['location.country'] && !!errors['location.country'])
            }
          />
          {touched['location.city'] && errors['location.city'] && (
            <ValidationMessage>{errors['location.city']}</ValidationMessage>
          )}
          {touched['location.state'] && errors['location.state'] && (
            <ValidationMessage>{errors['location.state']}</ValidationMessage>
          )}
          {touched['location.country'] && errors['location.country'] && (
            <ValidationMessage>{errors['location.country']}</ValidationMessage>
          )}
        </FormGroup>

        <FormRow>
          <FormColumn>
            <FormGroup>
              <FormLabel>
                Occupation <RequiredIndicator>*</RequiredIndicator>
              </FormLabel>
              <FormInput
                type="text"
                value={formData.occupation}
                onChange={(e) => handleFieldChange('occupation', e.target.value)}
                placeholder="What do you do for work?"
                $hasError={touched.occupation && !!errors.occupation}
              />
              {touched.occupation && errors.occupation && (
                <ValidationMessage>{errors.occupation}</ValidationMessage>
              )}
            </FormGroup>
          </FormColumn>

          <FormColumn>
            <FormGroup>
              <FormLabel>
                Education <RequiredIndicator>*</RequiredIndicator>
              </FormLabel>
              <FormInput
                type="text"
                value={formData.education}
                onChange={(e) => handleFieldChange('education', e.target.value)}
                placeholder="Your highest level of education"
                $hasError={touched.education && !!errors.education}
              />
              {touched.education && errors.education && (
                <ValidationMessage>{errors.education}</ValidationMessage>
              )}
            </FormGroup>
          </FormColumn>
        </FormRow>
      </FormContainer>
    </StepContainer>
  );
};
